import { useNavigate } from 'react-router';
import { putClientPolicyApplicationStepRequest, useNavigateToPage, selectCachedFormConfig } from '@client';
import { OnboardingPolicyApplicationDashboard } from '@client';
import { fullName } from '@shared';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '@client/model';

interface UsePolicyApplicationStepsProps {
  clientId?: string;
  clientPolicyApplicationId?: string;
  underwritingFormId?: string;
  applicationFormId?: string;
  client: Client;
  policyApplication: ClientPolicyApplication | undefined;
}

function getApplicationFormValue(
  policyApplication: ClientPolicyApplication | undefined,
  questionRef: string,
): string | number | boolean | null {
  if (!policyApplication?.steps) return null;

  const applicationStep = policyApplication.steps.find(
    step => step.stepType === 'application-form',
  );

  if (!applicationStep?.clientPolicyApplicationForm?.submissionData) return null;

  const value = applicationStep.clientPolicyApplicationForm.submissionData[questionRef];

  // Check if value is an object (including arrays, but not null)
  if (typeof value === 'object' && value !== null) {
    // eslint-disable-next-line no-console
    console.warn(
      `Warning: getApplicationFormValue received an object for questionRef "${questionRef}". Returning null.`,
      value,
    );
    return null;
  }

  return value ?? null;
}

function resolveParameterValue(
  paramKey: string,
  client: Client,
  policyApplication: ClientPolicyApplication | undefined,
): string | number | boolean | null {
  switch (paramKey) {
    case 'clientFullName':
      return fullName(client.clientFirstName, client.clientLastName);
    case 'clientPartnerFullName':
      return client.mutableClientPartner
        ? fullName(
            client.mutableClientPartner.clientFirstName,
            client.mutableClientPartner.clientLastName,
          )
        : null;
    case 'fundingPolicyType':
      return policyApplication?.fundingPolicyType ?? null;
    default: {
      return getApplicationFormValue(policyApplication, paramKey);
    }
  }
}

/**
 * Builds a form URL with required query parameters
 *
 * Example usage:
 * - For a form requiring 'policy_owners' parameter, ensure the form definition includes:
 *   requiredParameters: [
 *     { key: 'policy_owners', type: 'string', required: true, description: 'Policy owner selection' }
 *   ]
 * - The parameter will be resolved from the pre-application form's 'policyOwners' questionRef
 * - URL will include: ?policy_owners=Joint%20ownership (if that was selected in pre-app form)
 */
function buildFormUrlWithParameters(
  baseUrl: string,
  formDefinition: PolicyFormDefinition | undefined,
  client: Client,
  policyApplication: ClientPolicyApplication | undefined,
): string {
  // If no form definition or no required parameters, return base URL
  if (!formDefinition?.requiredParameters || formDefinition.requiredParameters.length === 0) {
    return baseUrl;
  }

  const queryParams = new URLSearchParams();

  for (const param of formDefinition.requiredParameters) {
    const resolvedValue = resolveParameterValue(param.key, client, policyApplication);

    if (param.required && !resolvedValue) {
      continue;
    }

    if (resolvedValue) {
      queryParams.set(param.key, encodeURIComponent(resolvedValue));
    }
  }

  const queryString = queryParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Helper function to build form navigation URLs with parameters
 */
function buildFormNavigationUrl(
  formType: 'underwriting' | 'pre-application' | 'application',
  clientId: string,
  clientPolicyApplicationId: string,
  formId: string,
  forClientId: string,
  client: Client,
  policyApplication: ClientPolicyApplication | undefined,
  formDefinition: PolicyFormDefinition | undefined,
): string {
  const baseUrl = `/clients/${clientId}/policy-form/${clientPolicyApplicationId}/${formId}/${forClientId}`;
  return buildFormUrlWithParameters(baseUrl, formDefinition, client, policyApplication);
}

export function usePolicyApplicationSteps({
  clientId,
  clientPolicyApplicationId,
  underwritingFormId,
  applicationFormId,
  client,
  policyApplication,
}: UsePolicyApplicationStepsProps) {
  const navigate = useNavigate();
  const navigateToPage = useNavigateToPage();
  const dispatch = useDispatch();

  // Get form definitions from Redux cache (fetched from database API based on formId)
  const underwritingFormConfig = useSelector((state: AppState) =>
    underwritingFormId ? selectCachedFormConfig(state, underwritingFormId) : undefined
  );
  const applicationFormConfig = useSelector((state: AppState) =>
    applicationFormId ? selectCachedFormConfig(state, applicationFormId) : undefined
  );

  const handleGoHome = () => {
    if (clientId) {
      navigateToPage(OnboardingPolicyApplicationDashboard, { clientId });
    }
  };

  const handleStartUnderwritingForm = (forClientId: string) => {
    if (underwritingFormId && clientId && clientPolicyApplicationId) {
      const underwritingFormUrl = buildFormNavigationUrl(
        'underwriting',
        clientId,
        clientPolicyApplicationId,
        underwritingFormId,
        forClientId,
        client,
        policyApplication,
        underwritingFormConfig?.formDefinition,
      );
      navigate(underwritingFormUrl);
    }
  };

  const handleSkipUnderwritingForm = (stepType: PolicyApplicationStepType) => {
    if (clientPolicyApplicationId && stepType) {
      dispatch(
        putClientPolicyApplicationStepRequest({
          clientPolicyApplicationId,
          stepType,
          status: 'skipped',
        }),
      );
    }
  };

  const handleStartApplicationForm = () => {
    if (applicationFormId && clientId && clientPolicyApplicationId) {
      const applicationFormUrl = buildFormNavigationUrl(
        'application',
        clientId,
        clientPolicyApplicationId,
        applicationFormId,
        clientId,
        client,
        policyApplication,
        applicationFormConfig?.formDefinition,
      );
      navigate(applicationFormUrl);
    }
  };

  return {
    handleGoHome,
    handleStartUnderwritingForm,
    handleStartApplicationForm,
    handleSkipUnderwritingForm,
  };
}